import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  integrationAccountKeys,
  integrationDemandLinkKeys,
  integrationResourceKeys,
  integrationSolutionKeys,
  integrationTimeEntryKeys,
  solutionWorkloadKeys,
} from "@/lib/query";
import type { RefreshAccountsResult } from "./use-integration-accounts";
import type { RefreshResourcesResult } from "./use-integration-resources";
import type { RefreshDemandLinksResult } from "./use-integration-solution-demands";
import type { RefreshSolutionsResult } from "./use-integration-solutions";
import type { RefreshTimeEntriesResult } from "./use-integration-time-entries";

async function postRefresh<T>(url: string): Promise<T> {
  const res = await fetch(url, { method: "POST" });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export type RefreshAllResult = {
  solutions: RefreshSolutionsResult;
  accounts: RefreshAccountsResult;
  resources: RefreshResourcesResult;
  demandLinks: RefreshDemandLinksResult;
  timeEntries: RefreshTimeEntriesResult;
};

/**
 * Refresh every NOS-backed directory in one action — Solutions, Accounts,
 * Resources, Demand links, and Time Entry — then invalidate all lists and
 * surface a single combined toast. Time Entry runs LAST (after the demand-links
 * refresh) so hours fan against freshly-synced demand links in a single run.
 */
export function useRefreshAllIntegrations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RefreshAllResult> => {
      const [solutions, accounts, resources, demandLinks] = await Promise.all([
        postRefresh<RefreshSolutionsResult>("/api/integrations/solutions/refresh"),
        postRefresh<RefreshAccountsResult>("/api/integrations/accounts/refresh"),
        postRefresh<RefreshResourcesResult>("/api/integrations/resources/refresh"),
        postRefresh<RefreshDemandLinksResult>("/api/integrations/demand-links/refresh"),
      ]);
      // Hours map through solution_demand_link, so sync them after the links exist.
      const timeEntries = await postRefresh<RefreshTimeEntriesResult>(
        "/api/integrations/time-entries/refresh",
      );
      return { solutions, accounts, resources, demandLinks, timeEntries };
    },
    onSuccess: ({ solutions, accounts, resources, demandLinks, timeEntries }) => {
      qc.invalidateQueries({ queryKey: integrationSolutionKeys.all });
      qc.invalidateQueries({ queryKey: integrationAccountKeys.all });
      qc.invalidateQueries({ queryKey: integrationResourceKeys.all });
      qc.invalidateQueries({ queryKey: integrationDemandLinkKeys.all });
      qc.invalidateQueries({ queryKey: integrationTimeEntryKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      const errors =
        solutions.errors +
        solutions.constraintErrors +
        solutions.accountConstraintErrors +
        accounts.errors +
        resources.errors +
        demandLinks.errors +
        timeEntries.errors;
      toast.success(
        `Refreshed — ${solutions.fetched} Solutions (${solutions.newDiscovered} new), ` +
          `${accounts.fetched} Accounts (${accounts.newDiscovered} new), ` +
          `${resources.fetched} Resources (${resources.created} new), ` +
          `${demandLinks.fetched} Demand links (${demandLinks.created} new), ` +
          `${timeEntries.fetched} Time Entries` +
          (errors > 0 ? `, ${errors} errors` : ""),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
